import { useCallback, useState } from 'react'
import { archiveIssue } from 'components/canary/issue_tracking/issueTrackingIssuesApi'

interface UseArchiveIssueConfirmationOptions {
  archiveErrorMessage: string
  onArchiveStart?: () => void
  onArchived: (issueId: number) => void | Promise<void>
}

export interface ArchiveIssueConfirmationState {
  archiveWarningIssueId: number | null
  archiveWarningError: string | null
  archivingIssueId: number | null
  cancelArchiveIssue: () => void
  confirmArchiveIssue: () => Promise<void>
}

export interface ArchiveIssueConfirmationControls extends ArchiveIssueConfirmationState {
  requestArchiveIssue: (issueId: number) => void
}

export const useArchiveIssueConfirmation = ({
  archiveErrorMessage,
  onArchiveStart,
  onArchived
}: UseArchiveIssueConfirmationOptions): ArchiveIssueConfirmationControls => {
  const [archiveWarningIssueId, setArchiveWarningIssueId] = useState<number | null>(null)
  const [archiveWarningError, setArchiveWarningError] = useState<string | null>(null)
  const [archivingIssueId, setArchivingIssueId] = useState<number | null>(null)

  const requestArchiveIssue = useCallback((issueId: number): void => {
    setArchiveWarningError(null)
    setArchiveWarningIssueId(issueId)
  }, [])

  const cancelArchiveIssue = useCallback((): void => {
    if (archivingIssueId != null) return

    setArchiveWarningError(null)
    setArchiveWarningIssueId(null)
  }, [archivingIssueId])

  const confirmArchiveIssue = useCallback(async (): Promise<void> => {
    if (archiveWarningIssueId == null || archivingIssueId != null) return

    const issueId = archiveWarningIssueId
    setArchivingIssueId(issueId)
    setArchiveWarningError(null)
    onArchiveStart?.()

    try {
      await archiveIssue(issueId)
    } catch (_error) {
      setArchiveWarningError(archiveErrorMessage)
      setArchivingIssueId(null)
      return
    }

    setArchiveWarningIssueId(null)

    try {
      await onArchived(issueId)
    } finally {
      setArchivingIssueId(null)
    }
  }, [archiveErrorMessage, archiveWarningIssueId, archivingIssueId, onArchiveStart, onArchived])

  return {
    archiveWarningIssueId,
    archiveWarningError,
    archivingIssueId,
    requestArchiveIssue,
    cancelArchiveIssue,
    confirmArchiveIssue
  }
}
